import { useEffect, useState } from 'react';

/**
 * PROTOTYPE — throwaway. Question: which type pairing should bQuest use?
 *
 * Three font variants applied to the whole app, picked from a floating chip:
 *   A — current: Termina (sans) + Recoleta (serif headings)
 *   B — Figma proposal: Newsreader (headings) + Source Sans 3 (body, buttons, labels)
 *   C — Figma proposal with Geist: Newsreader (headings) + Geist (body) + Geist Mono (code/numbers)
 *
 * Shown in dev and in builds with VITE_FONT_PROTOTYPE=true (UAT only). Choice
 * lives in localStorage so it survives navigation/refresh; `?font=B` also sets it.
 * B/C override every font in the app with !important rules scoped to
 * html[data-font-proto], so A is exactly what ships today.
 * Delete this file, its mount in App.tsx and the UAT workflow line once a
 * variant wins.
 */

type Variant = 'A' | 'B' | 'C';

const VARIANTS: { key: Variant; name: string; detail: string }[] = [
  { key: 'A', name: 'Current', detail: 'Termina + Recoleta' },
  { key: 'B', name: 'Figma', detail: 'Newsreader + Source Sans 3' },
  { key: 'C', name: 'Figma + Geist', detail: 'Newsreader + Geist / Geist Mono' },
];

const STORAGE_KEY = 'bquest_font_prototype';

const GOOGLE_FONTS_HREF =
  'https://fonts.googleapis.com/css2?family=Newsreader:ital,opsz,wght@0,6..72,200..800;1,6..72,200..800&family=Source+Sans+3:ital,wght@0,200..900;1,200..900&family=Geist:wght@100..900&family=Geist+Mono:wght@100..900&display=swap';

const HEADINGS = 'h1, h2, h3, [class*="font-recoleta"]';

const overrides = (v: Variant, body: string, mono?: string) => `
html[data-font-proto="${v}"] *:not(svg *) { font-family: ${body} !important; }
html[data-font-proto="${v}"] :is(${HEADINGS}) { font-family: 'Newsreader', serif !important; }
${mono ? `html[data-font-proto="${v}"] :is(code, pre, kbd, samp, .font-mono, .tabular-nums) { font-family: ${mono} !important; }` : ''}
`;

const PROTOTYPE_CSS = `
${overrides('B', "'Source Sans 3', sans-serif")}
${overrides('C', "'Geist', sans-serif", "'Geist Mono', monospace")}
html[data-font-proto] .font-recoleta-thin { font-weight: 200; }
html[data-font-proto] .font-recoleta-light { font-weight: 300; }
html[data-font-proto] .font-recoleta-medium { font-weight: 500; }
html[data-font-proto] .font-recoleta-bold { font-weight: 700; }
html[data-font-proto] .font-recoleta-black { font-weight: 800; }
#font-prototype-switcher, #font-prototype-switcher * {
  font-family: ui-sans-serif, system-ui, sans-serif !important;
}
`;

const isEnabled = () =>
  import.meta.env.DEV || import.meta.env.VITE_FONT_PROTOTYPE?.trim() === 'true';

function isVariant(value: string | null): value is Variant {
  return value === 'A' || value === 'B' || value === 'C';
}

function readInitial(): Variant {
  const fromUrl = new URLSearchParams(window.location.search).get('font')?.toUpperCase() ?? null;
  if (isVariant(fromUrl)) return fromUrl;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isVariant(stored) ? stored : 'A';
  } catch {
    return 'A';
  }
}

const isTyping = (el: Element | null) =>
  !!el &&
  (el.tagName === 'INPUT' ||
    el.tagName === 'TEXTAREA' ||
    el.tagName === 'SELECT' ||
    (el as HTMLElement).isContentEditable ||
    el.getAttribute('role') === 'slider');

export const FontPrototypeSwitcher = () => (isEnabled() ? <Switcher /> : null);

const Switcher = () => {
  const [variant, setVariant] = useState<Variant>(readInitial);
  const [open, setOpen] = useState(false);

  // Only B/C need the Google Fonts; A stays a true no-op.
  useEffect(() => {
    if (variant === 'A' || document.getElementById('font-prototype-fonts')) return;
    const link = document.createElement('link');
    link.id = 'font-prototype-fonts';
    link.rel = 'stylesheet';
    link.href = GOOGLE_FONTS_HREF;
    document.head.appendChild(link);
  }, [variant]);

  useEffect(() => {
    const root = document.documentElement;
    if (variant === 'A') root.removeAttribute('data-font-proto');
    else root.setAttribute('data-font-proto', variant);
    try {
      localStorage.setItem(STORAGE_KEY, variant);
    } catch {
      /* storage unavailable — choice lasts until reload */
    }
    console.info('[font-prototype]', variant, VARIANTS.find((v) => v.key === variant)?.detail);
  }, [variant]);

  // Alt+←/→ cycles variants (plain arrows belong to the app's own widgets).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!e.altKey || isTyping(document.activeElement)) return;
      const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
      if (!step) return;
      e.preventDefault();
      setVariant((current) => {
        const i = VARIANTS.findIndex((v) => v.key === current);
        return VARIANTS[(i + step + VARIANTS.length) % VARIANTS.length].key;
      });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const current = VARIANTS.find((v) => v.key === variant)!;

  return (
    <div
      id="font-prototype-switcher"
      className="fixed bottom-4 left-4 z-[2147483000] text-sm text-white"
    >
      <style>{PROTOTYPE_CSS}</style>
      {open && (
        <div
          role="dialog"
          aria-label="Font prototype"
          className="mb-2 w-72 rounded-xl bg-slate-900 p-3 shadow-2xl ring-1 ring-white/20"
        >
          <p className="mb-2 text-xs uppercase tracking-wider text-white/60">
            Font prototype · UAT only
          </p>
          <div className="flex flex-col gap-1.5">
            {VARIANTS.map((v) => (
              <button
                key={v.key}
                type="button"
                aria-pressed={v.key === variant}
                onClick={() => setVariant(v.key)}
                className={`rounded-lg px-3 py-2 text-left transition-colors ${
                  v.key === variant ? 'bg-amber-400 text-slate-950' : 'hover:bg-white/10'
                }`}
              >
                <span className="font-semibold">
                  {v.key} · {v.name}
                </span>
                <span className="block text-xs opacity-75">{v.detail}</span>
              </button>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-white/50">Alt + ← / → to cycle</p>
        </div>
      )}
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 shadow-2xl ring-1 ring-white/20 hover:bg-slate-800"
      >
        <span className="font-serif text-base leading-none">Aa</span>
        <span>
          Font {current.key} · {current.name}
        </span>
      </button>
    </div>
  );
};
